<template>
  <div class="number-pad-container">
    <div class="phone-display">{{ phoneNumber }}</div>
    <h3 class="phone-number-prompt" v-show="phoneNumber.length === 0">
      Enter Phone Number
    </h3>
    <div class="number-pad">
      <button
        v-for="key in keys"
        :key="key"
        class="number-key"
        @click="onKeyPress(key)"
      >
        {{ key }}
      </button>
    </div>
    <div class="bottom-row">
      <button class="clear-button" @click="clear">
        <i class="fas fa-delete-left"></i>
      </button>
      <button class="zero-button" @click="onKeyPress('0')">0</button>
      <button
        class="submit-button"
        @click="submitPhoneNumber"
        :disabled="isLoading"
      >
        <i v-if="!isLoading" class="fas fa-arrow-right"></i>
        <i v-else class="fas fa-spinner fa-spin"></i>
      </button>
    </div>
  </div>
</template>

<script>
import Swal from "sweetalert2";
import APIService from "@/service/APIService";
import { EventBus } from "@/eventBus";

export default {
  props: {
    initialPhoneNumber: {
      type: String,
      default: "",
    },
  },
  data() {
    return {
      phoneNumber: this.initialPhoneNumber,
      keys: ["1", "2", "3", "4", "5", "6", "7", "8", "9"],
      isSmallScreen: false,
      status: "re_phone_no",
      isLoading: false,
    };
  },
  mounted() {
    this.checkButtonWidth();
    window.addEventListener("resize", this.checkButtonWidth);
  },
  beforeUnmount() {
    window.removeEventListener("resize", this.checkButtonWidth);
  },
  methods: {
    onKeyPress(key) {
      if (this.phoneNumber.length < 15) {
        this.phoneNumber += key;
      }
    },
    clear() {
      this.phoneNumber = "";
    },
    submitPhoneNumber() {
      if (this.phoneNumber) {
        this.isLoading = true; // Start loading
        this.$emit("phone-number-added", this.phoneNumber);

        const urlParams = new URLSearchParams(window.location.search);
        const token = urlParams.get("token");

        new APIService(
          token,
          this.apiBaseURL,
          "customer-display/client/submit-phone-number"
        )
          .postData({ phone_number: this.phoneNumber })
          .then((response) => {
            if (response.status === 200) {
              const userDetails = {
                userName: response.customer.full_name,
                userProfilePicture: response.customer.s3_file_name,
                availablePoints: response.customer.total_rp,
              };
              this.$emit("update-status", "billing");
              EventBus.emit("user-details-received", userDetails);
              console.log("user", userDetails);
            }
          })
          .catch((error) => {
            if (error.response && error.response.status === 404) {
              Swal.fire({
                icon: "warning",
                title: "Number Not Found",
                text: "The phone number you entered is not registered.",
                confirmButtonText: "OK",
                background: "#f8f9fa",
                iconColor: "#ffc107",
                confirmButtonColor: "#ffc107",
              });
            } else {
              Swal.fire({
                icon: "error",
                title: "Error",
                text: "Something went wrong. Please try again.",
                confirmButtonText: "OK",
                background: "#f8f9fa",
                iconColor: "#dc3545",
                confirmButtonColor: "#dc3545",
              });
            }
          })
          .finally(() => {
            this.isLoading = false; // Stop loading
          });
      } else {
        Swal.fire({
          icon: "error",
          title: "Oops...",
          text: "Please enter a phone number.",
          confirmButtonText: "OK",
          background: "#f8f9fa",
          iconColor: "#dc3545",
          confirmButtonColor: "#dc3545",
        });
      }
    },
    checkButtonWidth() {
      const numberPadButton = this.$el.querySelector(".number-key");
      const clearButton = this.$el.querySelector(".clear-button");
      const submitButton = this.$el.querySelector(".submit-button");

      if (numberPadButton && clearButton && submitButton) {
        const numberPadButtonWidth = numberPadButton.offsetWidth;
        const clearButtonWidth = clearButton.offsetWidth;
        const submitButtonWidth = submitButton.offsetWidth;

        this.isSmallScreen =
          clearButtonWidth > numberPadButtonWidth ||
          submitButtonWidth > numberPadButtonWidth;
      }
    },
  },
};
</script>

<style scoped>
.number-pad-container {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  height: 100%;
  background: #100d08;
  padding: 10px;
  box-sizing: border-box;
}

.phone-number-prompt {
  font-size: 1rem;
  font-weight: bold;
  color: #d9d7d4;
  text-shadow: 2px 2px 5px rgba(0, 0, 0, 0.6);
  margin-bottom: 20px;
  text-align: center;
  font-family: "Poppins", sans-serif;
}

.phone-display {
  font-size: 2.5rem;
  font-weight: bold;
  margin-bottom: 20px;
  border: 1px solid #221e1d;
  border-radius: 10px;
  padding: 20px;
  width: 50%;
  text-align: center;
  color: white;
  background-color: #100d08;
  height: 80px;
  display: flex;
  align-items: center;
  justify-content: center;
  overflow: hidden;
  box-sizing: border-box;
}

.number-pad {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 20px;
  width: 50%;
  margin-bottom: 20px;
}

.number-key {
  font-size: 2rem;
  font-weight: bold;
  color: #ffffff;
  background-color: #221e1d;
  border: none;
  border-radius: 50%;
  box-shadow: 2px 2px 5px rgba(0, 0, 0, 0.4);
  cursor: pointer;
  width: 80px;
  height: 80px;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.3s ease;
}

.number-key:hover {
  background-color: rgb(43, 41, 40);
  transform: scale(1.05);
}

.bottom-row {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 20px;
  width: 50%;
}

.clear-button,
.zero-button,
.submit-button {
  font-weight: bold;
  border: none;
  border-radius: 50%;
  cursor: pointer;
  height: 80px;
  width: 80px;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.3s ease;
}

.clear-button {
  background-color: #100d08;
  font-size: 1.5rem;
  color: #ffffff;
}

.clear-button:hover {
  background-color: rgb(30, 25, 18);
}

.zero-button {
  background-color: #221e1d;
  font-size: 2rem;
  color: #ffffff;
}

.zero-button:hover {
  background-color: rgb(43, 41, 40);
}

.submit-button {
  background-color: #fdb515;
  font-size: 1.5rem;
  color: #100e0d;
}

.submit-button:hover {
  background-color: rgb(43, 41, 40);
}

/* Adjust icon size for small screens */
@media (max-width: 600px) {
  .clear-button i,
  .submit-button i {
    font-size: 3rem; /* Icon size */
  }

  .clear-button span,
  .submit-button span {
    display: none; /* Hide text on small screens */
  }
}
</style>
